import React, { Fragment, useState } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import 'url-search-params-polyfill';
import {
  PageHeader,
  SimpleDataTable,
  Section,
  slugify,
  findObjIndexByKey,
  makeBreadcrumb,
  GatsbyLink,
  TabContainer,
  Tab,
  getObjByValue
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import PlanFilter from '../components/PlanFilter';

const GovLevelOne = (props) => {
  const { location, data, pageContext } = props;
  const { content, site, tabsNav } = data;
  const { beneficiaryTable } = content;
  const { summaryData } = pageContext;
  const tabs = tabsNav.navItems;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent.parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, '');
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: '/beneficiary-agency',
    text: 'Plan by Beneficiary Agency',
    sections: [content.govLevelOne]
  });
  const layoutProps = {
    activeIndex: 1,
    pre: () => <PlanFilter path={relPath} />,
    above: () => <PageHeader title={content.title} />,
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime,
    containerClasses: 'main-content main-content--two'
  };

  const { headers, headersBySource } = beneficiaryTable;
  const createTableData = (tabData, dataKey) => {
    const h = (dataKey === 'bySourceTab') ? headersBySource : headers;
    const { data: tableData, value: tabValue } = tabData;
    // override table data first column value
    const overrideKey = 'govLevelTwo';
    // data - govLevelTwo total data
    const newTableData = tableData.map((d) => {
      const text = d['govLevelTwoReal'];
      const href = `${content.fields.slug}/${slugify(text)}`;
      return Object.assign({}, d, {
        [overrideKey] : <GatsbyLink to={href}>{text}</GatsbyLink>
      });
    });
    // total - summary row data
    const summaryObj = getObjByValue(summaryData[dataKey], tabValue, 'value')
    const summaryArr = summaryObj && summaryObj.data
    const summary = summaryArr && getObjByValue(summaryArr, content.govLevelOne, 'govLevelOne')
    const total = Object.assign({}, summary, {
      [overrideKey]: 'Total'
    })
    const tableProps = { data: newTableData, total, headers: h };
    return tableProps;
  }


  const urlToTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const tabData = tabs;
      index = findObjIndexByKey({ arr: tabData, value:tabParam, key: tabKey, slug: true });
    }
    return index;
  };

  const urlToSubTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    const subTabParam = params.get('subtab');
    if (tabParam && subTabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const subTabKey = 'name';
      const parentTab = getObjByValue(tabs, tabParam, tabKey);
      const subTabData = beneficiaryTable[parentTab.dataKey];
      index = findObjIndexByKey({ arr: subTabData, value: subTabParam, key: subTabKey, slug: true});
    }
    return index;
  };

  const [ tabIndex, setTabIndex ] = useState(urlToTabIndex(location));
  const [ subTabIndex, setSubTabIndex ] = useState(urlToSubTabIndex(location));

  const onTabChange = (tabRef, curLocation) => {
    const { location: newLocation } = props;
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = newLocation;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: tabs, value: tab, key: 'text'});
      setTabIndex(index);
      // reset sub tab on tab change
      setSubTabIndex(0);
    }
  };

  const onSubTabChange = ({tabRef, location: curLocation, parentTab}) => {
    const { location } = props;
    if (global.window && curLocation.href && tabRef.current) {
      const subTab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${parentTab.href}&subtab=${slugify(subTab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: beneficiaryTable[parentTab.dataKey], value: subTab, key: 'name'});
      setSubTabIndex(index);
    }
  };

  // metadata for the page
  const metaTitle = content.title;
  const metaDescription = `Capital investment plan by beneficiary state organization - ${content.title}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        {beneficiaryTable
          && (
          <Section>
            <React.Fragment>
              <TabContainer
                defaultTab={tabIndex}
                onTabChange={(tabRef) => onTabChange(tabRef, location)}
              >
                {
                  tabs.map((tab) => (
                    <Tab 
                    handleClick={function noRefCheck(){}}
                    title={tab.text}
                    key={`tab-${tab.href}`}
                  >
                      <TabContainer
                        nested
                        defaultTab={subTabIndex}
                        onTabChange={(tabRef) => onSubTabChange({ tabRef, location, parentTab: tab })}
                      >
                        {
                          beneficiaryTable[tab.dataKey].map((subTab) => 
                            (
                            <Tab
                              title={subTab.name}
                              key={`subtab-${subTab.value}`}
                            >
                              <SimpleDataTable title={content.title} table={createTableData(subTab, tab.dataKey)} maxColWidth={375} download={`${location.pathname}/${subTab.name}`} />
                            </Tab>
                          ))
                        }
                      </TabContainer>
                    </Tab>
                  ))
                }
              </TabContainer>
            </React.Fragment>
          </Section>
          )
        }
      </Layout>
    </Fragment>
  );
};

GovLevelOne.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default GovLevelOne;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    tabsNav: navJson(navType: {eq: "tabs"}) {
      ...SiteNav
    }
    content: planGovLevelOne(fields: { slug: { eq: $slug } }) {
      title
      budgetSection
      govLevelOne
      fields {
        slug
      }
      beneficiaryTable {
        headers {
          name
          type
          align
          value
        }
        headersBySource {
          name
          type
          align
          value
        }
        byYearTab {
          name
          value
          data {
            federalFunds
            govLevelTwo
            govLevelTwoReal
            nonBondCap
            operatingFunds
            otherFunds
            stateBondCap
            total
            year
          }
        }
        bySourceTab {
          name
          value
          data {
            fy1
            fy2
            fy3
            fy4
            fy5
            fy6
            govLevelTwo
            govLevelTwoReal
          }
        }
      }
      # Get the last modified timestamp for the file powering the plan item pages
      ...PlanGovLevelOneDataTimeStamp
    }
  }
`;
